import React, { useState } from "react";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/InputDefault";
import IconProvider from "@/components/ui/IconProvider";
import {
  DataSourceType,
  EXPANDABLE_DATA_TYPES,
  getLanguageFlag,
  getLanguageName,
} from "@/lib/dataSources";
import { getSelectedGame } from "@/lib/stores/unifiedUserStore";
import { createTranslationLine } from "@/lib/stores/stringsStore";
import { useToast } from "@/hooks/use-toast";
import { CircleFlagLanguage } from "react-circle-flags";
import { Tooltip } from "@/components/user-feedback/tooltip";

import { usePlatformTranslation } from '@/hooks/usePlatformTranslation';
interface ExpandableStringsTableContentProps {
  rowData: Record<string, React.ReactNode>;
  dataSource: DataSourceType;
  onCellUpdate?: (rowData: Record<string, React.ReactNode>, fieldPath: string, newValue: string) => void;
}

const ExpandableStringsTableContent: React.FC<ExpandableStringsTableContentProps> = ({
  rowData,
  dataSource,
  onCellUpdate,
}) => {
  const expandableConfig = EXPANDABLE_DATA_TYPES[dataSource];
  const selectedGame = getSelectedGame();
  const { toast } = useToast();

  // Inline editing state
  const [editingCell, setEditingCell] = useState<{
    languageCode: string;
    fieldKey: string;
    value: string;
  } | null>(null);

  if (!expandableConfig) {
    return (
      <div className="w-full h-[60px] bg-background border-b border-input flex items-center px-4">
        <div className="text-sm text-muted-foreground">
          No expandable content configured for {dataSource}
        </div>
      </div>
    );
  }

  // Get the data to display based on the dataPath
  const displayData = rowData[expandableConfig.dataPath];

  // Get supported languages from the selected game
  const supportedLanguages = selectedGame?.settings?.languages || [];
  const defaultLanguage = selectedGame?.settings?.defaultLanguage || "en";

  // Create a map of existing translations by language code
  const translationsMap = new Map();
  if (Array.isArray(displayData)) {
    displayData.forEach((translation: any) => {
      translationsMap.set(translation.language_code, translation);
    });
  }

  // Create a complete list of languages to display (supported + existing translations) in the table
  // Exclude the default language since it's already shown in the main row
  const allLanguages = new Set([...supportedLanguages]);
  if (Array.isArray(displayData)) {
    displayData.forEach((translation: any) => {
      allLanguages.add(translation.language_code);
    });
  }

  // Filter out the default language
  const languagesToDisplay = Array.from(allLanguages).filter(
    (lang) => lang.code !== defaultLanguage
  );

  // Handle inline editing
  const handleCellEdit = async (languageCode: string, fieldKey: string, currentValue: string) => {
    // Check if this language doesn't have a translation yet
    const translation = translationsMap.get(languageCode);
    if (!translation) {
      // Check if this language is supported by the game
      const isSupportedLanguage = supportedLanguages.some(lang => lang.code === languageCode);
      
      if (isSupportedLanguage) {
        try {
          // Create a new translation through the API
          const newTranslation = await createTranslationLine({
            dialogue_line_id: rowData.id as string,
            language_code: languageCode,
            translated_text: '',
            approved: false,
          }, toast);
          
          // Add the new translation to the translations map
          translationsMap.set(languageCode, newTranslation);
          
          // Update the display data with the new translation
          const currentTranslations = Array.isArray(displayData) ? [...displayData] : [];
          currentTranslations.push(newTranslation);
          
          // Update the rowData with the new translation
          const updatedRowData = {
            ...rowData,
            [expandableConfig.dataPath]: currentTranslations,
          };
          
          // Call onCellUpdate to update the parent component
          if (onCellUpdate) {
            onCellUpdate(updatedRowData, `${expandableConfig.dataPath}.${languageCode}.${fieldKey}`, currentValue);
          }
          
          toast({
            title: 'Translation Created',
            description: `Created new translation for ${getLanguageName(languageCode)}`,
            variant: 'default',
          });
        } catch (error) {
          console.error('Failed to create translation:', error);
          toast({
            title: 'Error',
            description: 'Failed to create new translation',
            variant: 'destructive',
          });
          return; // Don't proceed with editing if creation failed
        }
      } else {
        toast({
          title: 'Language Not Supported',
          description: `${getLanguageName(languageCode)} is not supported by this game`,
          variant: 'destructive',
        });
        return; // Don't proceed with editing for unsupported languages
      }
    }
    
    // Proceed with normal editing
    setEditingCell({
      languageCode,
      fieldKey,
      value: currentValue,
    });
  };

  const handleCellSave = (languageCode: string, fieldKey: string, newValue: string) => {
  const { t } = usePlatformTranslation();
    // Get the current value from the data
    const translation = translationsMap.get(languageCode);
    const currentValue = translation?.[fieldKey] || '';
    
    // Only update if the value has actually changed
    if (currentValue !== newValue) {
      if (onCellUpdate) {
        // Create the field path for nested data
        const fieldPath = `${expandableConfig.dataPath}.${languageCode}.${fieldKey}`;
        onCellUpdate(rowData, fieldPath, newValue);
      } else {
        console.log('❌ onCellUpdate not available in ExpandableTableContent');
      }
    } else {
      console.log('📝 No change detected, skipping update');
    }
    setEditingCell(null);
  };

  const handleCellCancel = () => {
    setEditingCell(null);
  };

  const handleCellKeyDown = (e: React.KeyboardEvent, languageCode: string, fieldKey: string) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleCellSave(languageCode, fieldKey, editingCell?.value || '');
    } else if (e.key === 'Escape') {
      e.preventDefault();
      handleCellCancel();
    }
  };

  // Helper function to render editable content
  const renderEditableContent = (languageCode: string, fieldKey: string, value: string, fieldConfig: any) => {
    if (fieldConfig.editable === "text" && typeof value === "string") {
      const isEditing = editingCell?.languageCode === languageCode && editingCell?.fieldKey === fieldKey;
      
      if (isEditing) {
        return (
          <div className="relative w-full">
            <Input
              value={editingCell.value}
              onChange={(e) => setEditingCell(prev => prev ? { ...prev, value: e.target.value } : null)}
              onKeyDown={(e) => handleCellKeyDown(e, languageCode, fieldKey)}
              onBlur={() => handleCellSave(languageCode, fieldKey, editingCell.value)}
              className="h-6 text-sm px-2 py-1 pr-12 w-full"
              placeholder={t('platform.cards.widgets.enter_translation')}
              autoFocus
            />
            <div className="absolute right-0 top-0 flex items-center h-6">
              <button
                onClick={() => handleCellSave(languageCode, fieldKey, editingCell.value)}
                  className="p-1 text-green-600 hover:text-green-700"
                title={t('platform.common.save')}
              >
                <IconProvider iconstring="fa-check" size={12} color="currentColor" />
              </button>
              <button
                onClick={handleCellCancel}
                className="p-1 text-destructive hover:text-destructive-foreground"
                title={t('platform.common.cancel')}
              >
                <IconProvider iconstring="fa-times" size={12} color="currentColor" />
              </button>
            </div>
          </div>
        );
      } else {
        return (
          <div 
            className="block truncate cursor-pointer hover:bg-muted px-1 py-0.5 rounded-sm"
            onClick={() => handleCellEdit(languageCode, fieldKey, value)}
            title={t('platform.cards.widgets.click_to_edit_title')}
          >
            {value || <span className="text-muted-foreground italic">{t('platform.cards.widgets.click_to_add_translation')}</span>}
          </div>
        );
      }
    }

    // Default rendering for non-editable fields
    return <span className="block truncate">{value}</span>;
  };

  return (
    <div className="w-full bg-[#F9F7FF] border- pt-8 border-gray-200">
      <div className="overflow-x-auto">
        <div 
          className="px-20 flex gap-4 min-w-max" 
          style={{ 
            minWidth: `${Math.min(languagesToDisplay.length, 4) * 240}px`,
            width: languagesToDisplay.length < 5 ? "100%" : "auto",
          }}
        >
          {/* Data path label */}
          <div className="flex-shrink-0 w-40 ">
            <div className="text-sm font-medium text-foreground truncate">
              {expandableConfig.dataPath.charAt(0).toUpperCase() +
                expandableConfig.dataPath.slice(1)}{" "}
              ({languagesToDisplay.length})
            </div>
          </div>
          
          {languagesToDisplay.map((languageCode) => {
            const translation = translationsMap.get(languageCode);
            const flag = getLanguageFlag(languageCode.code);

            return (
              <div
                key={languageCode.code}
                className={`  ${
                  languagesToDisplay.length < 5
                    ? "flex-1"
                    : "flex-shrink-0 w-60"
                }`}
              >
                <div className="flex items-start gap-8  ">
                  <div className="flex items-start gap-6">
                    <CircleFlagLanguage
                      languageCode={languageCode.code}
                      className="w-6 h-6 "
                    />
                    <div className="text-sm text-foreground min-h-[60px]">
                      {translation ? (
                        <div className="line-clamp-3">
                          {renderEditableContent(
                            languageCode.code, 
                            'translated_text', 
                            translation.translated_text || '',
                            expandableConfig.displayFields.find(f => f.key === 'translated_text')
                          )}
                        </div>
                      ) : (
                        <div 
                          className="text-muted-foreground italic cursor-pointer hover:bg-muted px-1 py-0.5 rounded-sm"
                          onClick={() => handleCellEdit(languageCode.code, 'translated_text', '')}
                          title={t('platform.cards.widgets.click_to_add_translation_title')}
                        >
                          No translation available
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default ExpandableStringsTableContent;
